import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  MailboxFolderSchema,
  type MailboxMessage,
} from "../../../../skills/leadcue-reply/scripts/lib/bison-mail-contracts.ts";
import { appDateTime } from "../../src/app-dates.ts";
import { LIST, resizeList } from "../../src/pane-layout.ts";
import { ExternalLinkIcon } from "../icons.tsx";
import { MailFrame } from "../mail/mail-frame.tsx";
import { prepareMailDisplay } from "../mail/mail-images.ts";
import { PaneDivider } from "../pane-divider.tsx";
import type { Shell } from "../shell/app-shell.tsx";
import { useTabTitle } from "../shell/tab-title.ts";
import { inboxApi } from "./api.ts";
import { rowTime } from "./list.tsx";
import { type FoldedPanel, FoldedPlaces, foldedPanelOf } from "./list-column.tsx";
import "./mailbox.css";

const FOLDERS = [
  ["all", "All mail"],
  ["inbox", "Inbox"],
  ["sent", "Sent"],
  ["spam", "Spam"],
  ["bounced", "Bounces"],
] as const;

function useMailbox(workspaceId: string, userId: string) {
  const [folder, setFolder] = useState<(typeof FOLDERS)[number][0]>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [continuation, setContinuation] = useState<string | null>(null);
  const [history, setHistory] = useState<(string | null)[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const page = useQuery({
    queryKey: ["inbox-v2", workspaceId, "native-mail", userId, folder, search, continuation],
    queryFn: ({ signal }) =>
      inboxApi.mailPage(workspaceId, { folder, search, continuation }, signal),
    retry: false,
  });
  const opened = useQuery({
    queryKey: ["inbox-v2", workspaceId, "native-message", userId, selected],
    queryFn: ({ signal }) =>
      selected === null
        ? Promise.reject(new Error("No message selected"))
        : inboxApi.mailMessage(workspaceId, selected, signal),
    enabled: selected !== null,
    retry: false,
  });
  const reset = () => {
    setContinuation(null);
    setHistory([]);
    setSelected(null);
  };
  return {
    page,
    opened,
    folder,
    search,
    searchInput,
    setSearchInput,
    selected,
    setSelected,
    pageNumber: history.length + 1,
    previousAvailable: history.length > 0,
    changeFolder(value: string) {
      setFolder(MailboxFolderSchema.parse(value));
      reset();
    },
    submitSearch() {
      const next = searchInput.trim();
      if (next === search && continuation === null) void page.refetch();
      else {
        setSearch(next);
        reset();
      }
    },
    previous() {
      const previous = history.at(-1);
      if (previous === undefined) return;
      setHistory(history.slice(0, -1));
      setContinuation(previous);
      setSelected(null);
    },
    next() {
      const next = page.data?.continuation;
      if (!next) return;
      setHistory([...history, continuation]);
      setContinuation(next);
      setSelected(null);
    },
  };
}
type MailboxState = ReturnType<typeof useMailbox>;

function MailboxList({ state, folded }: { state: MailboxState; folded: FoldedPanel | null }) {
  const { page } = state;
  return (
    <aside className="lcr-list lcr-folder-list" aria-label="All mail messages">
      {folded && <FoldedPlaces folded={folded} />}
      <div className="lcr-folder-head">
        <h2 className="lcr-folder-title">All mail</h2>
        <form
          className="lcr-mailbox-controls"
          onSubmit={(event) => {
            event.preventDefault();
            state.submitSearch();
          }}
        >
          <select
            aria-label="Mail folder"
            value={state.folder}
            onChange={(event) => state.changeFolder(event.currentTarget.value)}
          >
            {FOLDERS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            aria-label="Search mail"
            type="search"
            maxLength={200}
            value={state.searchInput}
            onChange={(event) => state.setSearchInput(event.currentTarget.value)}
            placeholder="Search mail"
          />
          <button type="submit" className="lcr-small-button">
            Search
          </button>
        </form>
      </div>
      <div className="lcr-list-body" aria-busy={page.isFetching || undefined}>
        {page.isPending && (
          <p className="lcr-list-empty" role="status">
            Loading mail…
          </p>
        )}
        {page.isError && (
          <p className="lcr-opening-failed" role="alert">
            Could not load mail.{" "}
            <button type="button" className="lcr-linklike" onClick={() => void page.refetch()}>
              Try again
            </button>
          </p>
        )}
        {page.data?.rows.length === 0 && (
          <p className="lcr-list-empty">
            {state.search ? "No messages found." : "No messages in this folder."}
          </p>
        )}
        {page.data?.rows.map((row) => (
          <div key={row.ref} className="lcr-row-wrap lcr-folder-row-wrap" data-plain>
            <button
              type="button"
              className="lcr-row lcr-folder-row"
              aria-current={state.selected === row.ref}
              onClick={() => state.setSelected(row.ref)}
            >
              <span className="lcr-row-line">
                <span className="lcr-row-subject">{row.subject || "No subject"}</span>
                <time
                  className="lcr-row-time"
                  dateTime={row.receivedAt}
                  title={appDateTime(row.receivedAt)}
                >
                  {rowTime(row.receivedAt)}
                </time>
              </span>
              <span className="lcr-row-lead">{row.sender || "Unknown sender"}</span>
              <span className="lcr-row-chips">{row.folder}</span>
            </button>
          </div>
        ))}
      </div>
      <nav className="lcr-mailbox-pager" aria-label="Mail pages">
        <button
          type="button"
          className="lcr-small-button"
          disabled={!state.previousAvailable || page.isFetching}
          onClick={state.previous}
        >
          Previous
        </button>
        <span>Page {state.pageNumber}</span>
        <button
          type="button"
          className="lcr-small-button"
          disabled={!page.data?.continuation || page.isFetching}
          onClick={state.next}
        >
          Next
        </button>
      </nav>
    </aside>
  );
}

function OpenMail({ message }: { message: MailboxMessage }) {
  const display = useMemo(
    () =>
      message.html
        ? prepareMailDisplay(message.html, [], (html) =>
            new DOMParser().parseFromString(html, "text/html"),
          )
        : null,
    [message.html],
  );
  return (
    <div className="lcr-folder-body">
      <div className="lcr-head-meta lcr-folder-meta">
        <p>From: {message.sender}</p>
        {message.to.length > 0 && <p>To: {message.to.join(", ")}</p>}
        {message.cc.length > 0 && <p>Cc: {message.cc.join(", ")}</p>}
        <time dateTime={message.receivedAt}>{appDateTime(message.receivedAt)}</time>
      </div>
      <div className="lcr-message lcr-received lcr-folder-message">
        {display ? (
          <MailFrame display={display} label={message.subject || "Message"} images={null} />
        ) : (
          <p className="lcr-folder-text">{message.text || "This message has no text."}</p>
        )}
      </div>
      {message.attachments.length > 0 && <p>Attachments: {message.attachments.join(", ")}</p>}
    </div>
  );
}

function MailboxReading({ state, onPhone }: { state: MailboxState; onPhone: boolean }) {
  const message = state.opened.data;
  return (
    <section
      className="lcr-detail lcr-i2-reading lcr-folder-reading-pane"
      aria-label="Mail reader"
      aria-busy={(state.selected !== null && state.opened.isPending) || undefined}
    >
      <header className="lcr-head lcr-i2-head lcr-folder-open-head">
        {onPhone && state.selected !== null && (
          <button
            type="button"
            className="lcr-small-button"
            onClick={() => state.setSelected(null)}
          >
            Back to mail
          </button>
        )}
        <h2>{message ? message.subject || "No subject" : "Choose a message"}</h2>
        {message?.bisonUrl && (
          <a
            className="lcr-linklike lcr-folder-bison"
            href={message.bisonUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLinkIcon size={16} /> Open in Bison
          </a>
        )}
      </header>
      {state.selected === null && (
        <div className="lcr-folder-nothing">
          <p>Choose a message to read it.</p>
        </div>
      )}
      {state.selected !== null && state.opened.isPending && (
        <p className="lcr-list-empty" role="status">
          Opening message…
        </p>
      )}
      {state.opened.isError && (
        <p className="lcr-opening-failed" role="alert">
          Could not open this message.{" "}
          <button
            type="button"
            className="lcr-linklike"
            onClick={() => void state.opened.refetch()}
          >
            Try again
          </button>
        </p>
      )}
      {message && <OpenMail message={message} />}
    </section>
  );
}

/** Native readable mail is available even when conversation/agent processing refused it. */
export function MailboxPage({ shell }: { shell: Shell }) {
  const state = useMailbox(shell.current.id, shell.user.id);
  useTabTitle(`All mail — ${shell.current.name}`);
  const widths: Record<string, string> = { "--lcr-list-w": `${shell.fitted.list}px` };
  return (
    <main
      className="lcr-main lcr-i2 lcr-folder-page lcr-mailbox-page"
      style={widths}
      data-phone={shell.onPhone || undefined}
      data-thread={state.selected !== null || undefined}
    >
      <h1 className="lcr-visually-hidden">All mail, {shell.current.name}</h1>
      <PaneDivider
        label="Resize mail list"
        className="lcr-divider-list"
        value={shell.fitted.list}
        min={LIST.min}
        max={LIST.max}
        fromPointer={(x) =>
          x - (document.querySelector(".lcr-mailbox-page")?.getBoundingClientRect().left ?? 0)
        }
        onResize={(width, done) => shell.changeLayout(resizeList(shell.layout, width), done)}
      />
      <MailboxList state={state} folded={foldedPanelOf(shell)} />
      <MailboxReading state={state} onPhone={shell.onPhone} />
    </main>
  );
}
